import { Pipe, PipeTransform, ɵstringify as stringify } from '@angular/core';
import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import { ALT_DATETIME_FORMAT, DATE_FORMAT } from '../constants/date';
import { AuthService } from '../services/auth.service';
import { isString } from '../utils/type-util';

/**
 * This pipe uses `date-fns-timezone` and `date-fns` date formatting
 * implementations for better timezone support.
 *
 * See docs here for more information about format strings, etc.:
 * * https://github.com/prantlf/date-fns-timezone
 * * https://github.com/date-fns/date-fns
 *
 * Order of precedence for timezone is as follows:
 * * Timezone passed in as a param to this pipe
 * * Authenticated user's timezone
 * * Browser/system timezone
 *
 * NOTE: UTC timezone is ALWAYS used if the date has a timestamp of 00:00:00 UTC and
 * it's being formatted using DATE_FORMAT, regardless of the timezone provided
 * as a parameter.
 */
@Pipe({
  name: 'date'
})
export class CustomDatePipe implements PipeTransform {
  constructor(private authService: AuthService) {}

  transform(value: Date | string | number, dateFormat: string, timezone?: string): string | null {
    if (value == null || value === '' || value !== value || !isValid(new Date(value))) {
      return null;
    }

    const utcFormattedDate = formatInTimeZone(value, 'UTC', ALT_DATETIME_FORMAT, { locale: enUS });

    if (utcFormattedDate.endsWith('00:00:00') && dateFormat === DATE_FORMAT) {
      timezone = 'UTC';
    } else {
      timezone = !timezone && this.authService.timezone ? this.authService.fullFormTimezone : timezone;
    }

    try {
      if (timezone) {
        return formatInTimeZone(value, timezone, dateFormat, { locale: enUS });
      } else {
        // Format using browser/system timezone

        return format(isString(value) ? parseISO(value) : value, dateFormat);
      }
    } catch (error) {
      throw Error(`InvalidPipeArgument: '${error.message}' for pipe '${stringify(CustomDatePipe)}'`);
    }
  }
}
